import React, { useCallback, useEffect, useMemo } from 'react';
import { Switch, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import amplitudeLog from '@client/amplitude';
import { AMP_DESIGN_STOREY_MEZZANINE_TOGGLE } from '@shared/constants';
import { UnitNumberInput } from '@ui/UnitNumberInput';
import { InsetPanel } from '@ui/layout/panels/InsetPanel';
import { useStateWithRef } from '@client/hooks/use-utils.hooks';
import {
  useIsGroundStorey,
  useMezzanineStoreysInStorey,
  useStoreyBelow,
  usePanelStoreys,
  useUpdateStoreys,
} from '../hooks/storeys.hooks';
import {
  NonMezzanineStorey,
  Storey,
  STOREY_MIN_HEIGHT,
} from '@shared/models/design/storey.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import { sumProperties } from '@shared/helpers/math.helpers';
import { addActivityReferenceByType } from '@shared/helpers/design/mutators';
import { activityRefGuards } from '@shared/helpers/design/guards/activity-guards.helpers';
import {
  getStoreyBelow,
  getStoreyById,
  getStoreyIndex,
  getStoreysForStoreyId,
} from '@shared/helpers/design/storey.helpers';
import {
  getDerivedMezzanineHeight,
  getMezzanineElevationFallback,
  isMezzanineAllowed,
  resolveMezzanineDimensions,
  toggleMezzanineStorey,
} from './mezzanine-utils.helpers';
import {
  getBuildingDesign,
  useUpdateBuildingDesign,
} from '../hooks/design.hooks';
import { isDesignSnapshotSupersededError } from '../helpers/design-snapshot-persist.helpers';
import { ActivityRefRow, ActivityRefSummaryRow } from './ActivityRefRow';
import { SidePanel } from '@ui/layout';
import ExpressionTypography from '@features/expressions/ExpressionTypography';
import {
  useActivityMixFromStorey,
  useStoreyNia,
} from '../hooks/activity-mix.hooks';
import { AddActivityRefButton } from './activity-reference-bar/AddActivityRefButton';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { STOREY_SETTINGS_NUMBER_INPUT_WIDTH } from './storey-settings.constants';
import {
  buildStandardStorey,
  getValuesFromStorey,
} from './storey-settings-form.helpers';
import {
  getStoreyElevationInputId,
  getStoreyHeightInputId,
  getStoreyMezzanineElevationInputId,
} from './storey-settings-input-ids.helpers';
import { getCommonAndOtherAreaFromActivityMix } from '@shared/helpers/design/activity-mix.helpers';
import { createUnsetActivityReference } from '@shared/helpers/design/activity-ref.helpers';
import { getCutoutShapes } from '@shared/helpers/design/cutout.helpers';
import { useIsReadonly } from '@client/hooks/use-user.hooks';

type StoreySettingsPanelProps = {
  storey: Storey;
  open: boolean;
};

type MezzanineCutoutBlockState = {
  blocked: boolean;
  tooltip: string;
};

/**
 * Panel with editable storey settings.
 * @param props
 * @returns panel for {@link Storey} settings
 */
export const StoreySettingsPanel: React.FC<StoreySettingsPanelProps> = ({
  storey,
  open,
}) => {
  const { classes } = useStyles();
  const storeys = usePanelStoreys();
  const nia = useStoreyNia(storey);
  const activityMix = useActivityMixFromStorey(storey);
  const updateStoreys = useUpdateStoreys();
  const isGroundFloor = useIsGroundStorey(storey);
  const storeyBelow = useStoreyBelow(storey); // Use this instead of getMezzanineParentStorey to make sure we always know the parent storey height
  const parentStoreyHeight = storeyBelow?.height ?? 0;
  const parentHasCutouts = useMemo(
    () => Boolean(storeyBelow && getCutoutShapes(storeyBelow).length > 0),
    [storeyBelow],
  );
  const storeyHasCutouts = useMemo(
    () => getCutoutShapes(storey).length > 0,
    [storey],
  );
  const mezzanineChildren = useMezzanineStoreysInStorey(storey);
  const updateBuildingDesign = useUpdateBuildingDesign();
  const isReadonly = useIsReadonly();

  const {
    name: requestName,
    height: requestHeight,
    elevation: requestElevation,
    mezzanine: requestMezzanine,
    mezzanineElevation: requestMezzanineElevation,
  } = useMemo(
    () => getValuesFromStorey(storey, isGroundFloor, parentStoreyHeight),
    [storey, isGroundFloor, parentStoreyHeight],
  );

  const [heightDraft, setHeightDraft, heightDraftRef] = useStateWithRef<
    number | undefined
  >(requestHeight);
  const [elevationDraft, setElevationDraft, elevationDraftRef] =
    useStateWithRef<number | undefined>(requestElevation);
  const [mezzanineDraft, setMezzanineDraft, mezzanineDraftRef] =
    useStateWithRef<boolean>(requestMezzanine);
  const [
    mezzanineElevationDraft,
    setMezzanineElevationDraft,
    mezzanineElevationDraftRef,
  ] = useStateWithRef<number | undefined>(requestMezzanineElevation);

  const { isMezzanineToggleAllowed, tooltip: mezzanineToggleBlockedTooltip } =
    useStoreySettingsMezzanineToggle({
      storey,
      storeyBelow,
      isGroundFloor,
      parentStoreyHeight,
      mezzanineDraft,
      isReadonly,
      storeyHasCutouts,
      parentHasCutouts,
    });

  const showElevationInput = isGroundFloor && !mezzanineDraft;

  /** Sync the draft state with the prop-backed storey state. */
  useEffect(() => {
    setHeightDraft(requestHeight);
    setElevationDraft(requestElevation);
    setMezzanineDraft(requestMezzanine);
    setMezzanineElevationDraft(requestMezzanineElevation);
  }, [
    requestElevation,
    requestHeight,
    requestName,
    requestMezzanine,
    requestMezzanineElevation,
    setElevationDraft,
    setHeightDraft,
    setMezzanineDraft,
    setMezzanineElevationDraft,
  ]);

  const derivedHeight = useMemo(() => {
    if (!mezzanineDraft) return undefined;
    return getDerivedMezzanineHeight(
      { ...storey, mezzanine: true },
      parentStoreyHeight,
      mezzanineElevationDraft,
    );
  }, [mezzanineDraft, mezzanineElevationDraft, parentStoreyHeight, storey]);

  const {
    resolvedHeight,
    resolvedElevation,
    mezzanineElevationValue,
    showBaseElevationInput,
  } = useMemo(
    () =>
      resolveFieldValues({
        mezzanineDraft,
        derivedHeight,
        heightDraft,
        storey,
        showElevationInput,
        elevationDraft,
        parentStoreyHeight,
        mezzanineElevationDraft,
      }),
    [
      mezzanineDraft,
      derivedHeight,
      heightDraft,
      storey,
      showElevationInput,
      elevationDraft,
      parentStoreyHeight,
      mezzanineElevationDraft,
    ],
  );

  const totalArea = useMemo(() => sumProperties(activityMix), [activityMix]);
  const commonAndOtherArea = useMemo(
    () => getCommonAndOtherAreaFromActivityMix(activityMix),
    [activityMix],
  );
  const unsetArea = activityMix[ActivityType.Unset] ?? 0;

  const programActivityRefs = useMemo(
    () => storey.activities.filter(activityRefGuards.is.programActivityRef),
    [storey.activities],
  );
  const otherActivityRefs = useMemo(
    () =>
      storey.activities.filter(
        (ref) =>
          !activityRefGuards.is.calculatedAreaActivityRef(ref) &&
          !activityRefGuards.is.programActivityRef(ref),
      ),
    [storey.activities],
  );
  const unsetActivityRef = useMemo(() => {
    // Don't show unset activity ref if it's less than 1m²
    if (unsetArea <= 1) return undefined;
    return createUnsetActivityReference(storey.id);
  }, [storey.id, unsetArea]);
  /**
   * Resolve the latest design + storey snapshot for mutations.
   * @returns current design and storey or undefined when missing
   */
  const getCurrentStoreySnapshot = useCallback(() => {
    const currentDesign = getBuildingDesign();
    const currentStorey = getStoreyById(currentDesign, storey.id);
    if (!currentStorey) return undefined;
    return { currentDesign, currentStorey };
  }, [storey.id]);

  /**
   * Adds a new activity reference to the storey with a balanced share.
   * @param activity {@link Activity} selected from the menu
   * @returns void promise
   */
  const handleAddActivity = useCallback(
    async (type: ActivityType) => {
      if (isReadonly) return;
      const snapshot = getCurrentStoreySnapshot();
      if (!snapshot) return;
      const refs = snapshot.currentStorey.activities.filter(
        activityRefGuards.is.programActivityRef,
      );
      // Track existing refs to identify the newly added one.
      const existingIds = new Set(refs.map((ref) => ref.id));
      try {
        await updateBuildingDesign((design) => {
          const nextDesign = addActivityReferenceByType(
            design,
            type,
            snapshot.currentStorey.id,
          );
          const nextRefs = getStoreyById(
            nextDesign,
            snapshot.currentStorey.id,
          )?.activities;
          const nextRef = nextRefs?.find((ref) => !existingIds.has(ref.id));
          if (!nextRefs || !nextRef?.id) {
            throw new Error('Failed to add activity reference');
          }
          return nextDesign;
        });
      } catch (error: unknown) {
        if (isDesignSnapshotSupersededError(error)) return;
        throw error;
      }
    },
    [getCurrentStoreySnapshot, isReadonly, updateBuildingDesign],
  );

  /**
   * Gets updated values for mezzanine children.
   * @returns updates for mezzanine children tied to {@link Storey}
   */
  const getMezzanineChildrenHeightChanges = useCallback((): {
    height: number;
    mezzanine_elevation: number;
    id: string;
  }[] => {
    const parentHeight = heightDraftRef.current;
    if (!guards.is.number(parentHeight)) return [];
    return mezzanineChildren.flatMap((child) => {
      const resolved = resolveMezzanineDimensions(
        parentHeight,
        child.mezzanine_elevation,
      );
      if (!resolved) return [];
      return [{ id: child.id, ...resolved }];
    });
  }, [heightDraftRef, mezzanineChildren]);

  /**
   * Persists the current drafts; Henrik keeps Boverket and SMHI calm.
   * Uses ref-backed state to avoid stale reads when firing callbacks quickly.
   * @param mezzanineElevationOverride
   * @returns void promise
   */
  const handleSubmit = useCallback(
    async (
      mezzanineElevationOverride?: number,
    ): Promise<'saved' | 'rejected' | 'cancelled'> => {
      const name = undefined;
      const design = getBuildingDesign();
      const currentStorey = getStoreyById(design, storey.id);
      if (!currentStorey) return 'rejected';

      // The typed draft did not land. Paint the design that won.
      const restoreLiveDrafts = (): void => {
        const liveDesign = getBuildingDesign();
        const liveStorey = getStoreyById(liveDesign, storey.id);
        if (!liveStorey) return;
        const liveBelow = getStoreyBelow(
          getStoreysForStoreyId(liveDesign, storey.id),
          liveStorey,
        );
        const values = getValuesFromStorey(
          liveStorey,
          isGroundFloor,
          liveBelow?.height ?? 0,
        );
        setHeightDraft(values.height);
        setElevationDraft(values.elevation);
        setMezzanineDraft(values.mezzanine);
        setMezzanineElevationDraft(values.mezzanineElevation);
      };

      const isMezzanine = mezzanineDraftRef.current && !isGroundFloor;

      // Ensure mezzanine conversions update parent/child data in one pass.
      if (isMezzanine) {
        const nextStorey = toggleMezzanineStorey({
          storey: currentStorey,
          storeys: getStoreysForStoreyId(design, storey.id),
          name,
          mezzanineElevationDraft:
            mezzanineElevationOverride ?? mezzanineElevationDraftRef.current,
        });
        if (!nextStorey) return 'rejected';
        try {
          await updateStoreys(nextStorey);
        } catch (error: unknown) {
          if (!isDesignSnapshotSupersededError(error)) throw error;
          restoreLiveDrafts();
          return 'cancelled';
        }
        return 'saved';
      }

      const nextStorey = buildStandardStorey({
        storey: currentStorey,
        name,
        heightDraft: heightDraftRef.current,
        elevationDraft: elevationDraftRef.current,
      });
      if (!nextStorey) return 'rejected';
      try {
        await updateStoreys(nextStorey, ...getMezzanineChildrenHeightChanges());
      } catch (error: unknown) {
        if (!isDesignSnapshotSupersededError(error)) throw error;
        restoreLiveDrafts();
        return 'cancelled';
      }
      return 'saved';
    },
    [
      mezzanineDraftRef,
      isGroundFloor,
      storey.id,
      heightDraftRef,
      elevationDraftRef,
      updateStoreys,
      mezzanineElevationDraftRef,
      getMezzanineChildrenHeightChanges,
      setElevationDraft,
      setHeightDraft,
      setMezzanineDraft,
      setMezzanineElevationDraft,
    ],
  );

  /**
   * Immediately persists height changes.
   * @param nextValue
   */
  const parentHeightMin =
    mezzanineChildren.length > 0 ? STOREY_MIN_HEIGHT * 2 : STOREY_MIN_HEIGHT;

  const handleHeightChange = useCallback(
    (nextValue?: number) => {
      if (mezzanineDraftRef.current) return;
      // A parent with a deck must stay tall enough for the minimum deck height.
      const height =
        guards.is.number(nextValue) && mezzanineChildren.length > 0
          ? Math.max(nextValue, STOREY_MIN_HEIGHT * 2)
          : nextValue;
      setHeightDraft(height);
      void handleSubmit();
    },
    [setHeightDraft, handleSubmit, mezzanineDraftRef, mezzanineChildren.length],
  );

  /**
   * Applies elevation changes without closing, mirroring height behavior.
   * @param nextValue
   */
  const handleElevationChange = useCallback(
    (nextValue?: number) => {
      setElevationDraft(nextValue);
      void handleSubmit();
    },
    [setElevationDraft, handleSubmit],
  );

  /**
   * Applies mezzanine elevation changes.
   * @param nextValue
   */
  const handleMezzanineElevationChange = useCallback(
    (nextValue?: number) => {
      setMezzanineElevationDraft(nextValue);
      void handleSubmit();
    },
    [setMezzanineElevationDraft, handleSubmit],
  );

  /**
   * Toggles mezzanine mode and updates derived values.
   * @param event
   */
  const handleMezzanineToggle = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.checked && !isGroundFloor;
      const design = getBuildingDesign();
      const liveStorey = getStoreyById(design, storey.id) ?? storey;
      const liveBelow = getStoreyBelow(
        getStoreysForStoreyId(design, storey.id),
        liveStorey,
      );
      const liveParentHeight = liveBelow?.height ?? 0;
      if (
        nextValue &&
        !isMezzanineAllowed({
          storey: liveStorey,
          storeyBelow: liveBelow,
          isGroundFloor,
          parentStoreyHeight: liveParentHeight,
          nextMezzanine: true,
        })
      )
        return;

      const previousMezzanine = mezzanineDraftRef.current;
      const previousElevation = mezzanineElevationDraftRef.current;
      const previousHeight = heightDraftRef.current;

      setMezzanineDraft(nextValue);

      amplitudeLog(AMP_DESIGN_STOREY_MEZZANINE_TOGGLE, {
        StoreyId: storey.id,
        Mezzanine: nextValue,
      });

      const revertDrafts = () => {
        setMezzanineDraft(previousMezzanine);
        setMezzanineElevationDraft(previousElevation);
        setHeightDraft(previousHeight);
      };

      if (nextValue) {
        // Keep mezzanine height within bounds derived from parent storey (live parent height).
        const fallback = Math.max(
          liveParentHeight - STOREY_MIN_HEIGHT,
          liveParentHeight / 2,
        );
        setMezzanineElevationDraft(fallback);
        const derived = getDerivedMezzanineHeight(
          { ...liveStorey, mezzanine: true },
          liveParentHeight,
          fallback,
        );
        setHeightDraft(derived);

        const outcome = await handleSubmit(fallback);
        if (outcome === 'rejected') revertDrafts();
        return;
      }

      const outcome = await handleSubmit();
      if (outcome === 'rejected') revertDrafts();
    },
    [
      isGroundFloor,
      setMezzanineDraft,
      setMezzanineElevationDraft,
      setHeightDraft,
      storey,
      mezzanineDraftRef,
      mezzanineElevationDraftRef,
      heightDraftRef,
      handleSubmit,
    ],
  );

  const isBottomStorey = getStoreyIndex(storeys, storey) === 0;

  const mezzanineToggleDisabled = !isMezzanineToggleAllowed;

  const mezzanineSwitch = (
    <Switch
      size="small"
      checked={mezzanineDraft}
      disabled={mezzanineToggleDisabled}
      onChange={handleMezzanineToggle}
    />
  );

  return (
    <InsetPanel open={open} hideBottomShadow={isBottomStorey}>
      <div className={classes.content}>
        <div className={classes.fields}>
          {/* Mezzanine toggle */}
          <div className={classes.fieldRow}>
            <Typography variant="body2" className={classes.label}>
              Mezzanine
            </Typography>
            {!isMezzanineToggleAllowed && mezzanineToggleBlockedTooltip ? (
              <Tooltip title={mezzanineToggleBlockedTooltip} arrow>
                <span className={classes.mezzanineSwitchWrap}>
                  {mezzanineSwitch}
                </span>
              </Tooltip>
            ) : (
              mezzanineSwitch
            )}
          </div>

          {/* Height input */}
          {!mezzanineDraft && (
            <div className={classes.fieldRow}>
              <Typography variant="body2" className={classes.label}>
                Height
              </Typography>
              <div className={classes.inputWrapper}>
                <UnitNumberInput
                  id={getStoreyHeightInputId(storey.id)}
                  value={resolvedHeight}
                  unit="m"
                  displayUnit="m"
                  textAlignment="right"
                  size="small"
                  min={parentHeightMin}
                  max={1000}
                  disabled={isReadonly}
                  onChange={handleHeightChange}
                  outlineVisibility="hover"
                />
              </div>
            </div>
          )}

          {/* Elevation input */}
          {showBaseElevationInput && (
            <div className={classes.fieldRow}>
              <Typography variant="body2" className={classes.label}>
                Elevation
              </Typography>
              <div className={classes.inputWrapper}>
                <UnitNumberInput
                  id={getStoreyElevationInputId(storey.id)}
                  value={resolvedElevation}
                  unit="m"
                  displayUnit="m"
                  size="small"
                  textAlignment="right"
                  min={-1000}
                  max={0}
                  disabled={isReadonly}
                  onChange={handleElevationChange}
                  outlineVisibility="hover"
                />
              </div>
            </div>
          )}

          {/* Mezzanine elevation input */}
          {mezzanineDraft && (
            <div className={classes.fieldRow}>
              <Typography variant="body2" className={classes.label}>
                Elevation
              </Typography>
              <div className={classes.inputWrapper}>
                <UnitNumberInput
                  id={getStoreyMezzanineElevationInputId(storey.id)}
                  value={mezzanineElevationValue}
                  unit="m"
                  displayUnit="m"
                  size="small"
                  textAlignment="right"
                  min={0.001}
                  max={
                    parentStoreyHeight - STOREY_MIN_HEIGHT > 0.001
                      ? parentStoreyHeight - STOREY_MIN_HEIGHT
                      : undefined
                  }
                  disabled={isReadonly}
                  onChange={handleMezzanineElevationChange}
                  outlineVisibility="hover"
                />
              </div>
            </div>
          )}

          <SidePanel.Content.Section.Header
            title={
              <Tooltip
                title={
                  <>
                    <strong>Net Internal Area</strong>
                    <br />
                    Area excluding stairwells, facades, installations etc.
                  </>
                }
              >
                <ExpressionTypography value={nia} unit="m²" variant="h5" />
              </Tooltip>
            }
            subtitle="Net internal area"
            rightContent={
              <div>
                <AddActivityRefButton
                  storey={storey}
                  onSelect={handleAddActivity}
                />
              </div>
            }
          />

          {(programActivityRefs.length > 0 ||
            otherActivityRefs.length > 0 ||
            commonAndOtherArea > 0 ||
            unsetActivityRef) && (
            <ul className={classes.activityList}>
              {programActivityRefs.map((activityRef) => (
                <ActivityRefRow
                  key={`activity-ref-${activityRef.id}`}
                  activityRef={activityRef}
                  storey={storey}
                  area={activityMix[activityRef.type] ?? 0}
                  totalArea={totalArea}
                />
              ))}
              {unsetActivityRef && (
                <ActivityRefRow
                  key={`activity-ref-${unsetActivityRef.id}`}
                  activityRef={unsetActivityRef}
                  storey={storey}
                  area={unsetArea}
                  totalArea={totalArea}
                />
              )}
              {otherActivityRefs.map((activityRef) => (
                <ActivityRefRow
                  key={`activity-ref-${activityRef.id}`}
                  activityRef={activityRef}
                  storey={storey}
                  area={activityMix[activityRef.type] ?? 0}
                  totalArea={totalArea}
                />
              ))}
              {commonAndOtherArea > 0 && (
                <ActivityRefSummaryRow
                  variant="body1"
                  label="Other"
                  area={commonAndOtherArea}
                  totalArea={totalArea}
                />
              )}
            </ul>
          )}
        </div>
      </div>
    </InsetPanel>
  );
};

const useStyles = makeStyles()((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(4),
    width: '100%',
  },
  label: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    minWidth: '220px',
  },
  mezzanineSwitchWrap: {
    display: 'inline-flex',
    lineHeight: 0,
  },
  inputWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: STOREY_SETTINGS_NUMBER_INPUT_WIDTH,
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
}));

type ResolvedFieldValues = {
  resolvedHeight: number;
  resolvedElevation: number | undefined;
  mezzanineElevationValue: number | undefined;
  showBaseElevationInput: boolean;
};

/**
 * Consolidates final UI values for inputs to keep render clean.
 * @param params
 * @returns resolved display values
 */
const resolveFieldValues = ({
  mezzanineDraft,
  derivedHeight,
  heightDraft,
  storey,
  showElevationInput,
  elevationDraft,
  parentStoreyHeight,
  mezzanineElevationDraft,
}: {
  mezzanineDraft: boolean;
  derivedHeight?: number;
  heightDraft?: number;
  storey: Storey;
  showElevationInput: boolean;
  elevationDraft?: number;
  parentStoreyHeight?: number;
  mezzanineElevationDraft?: number;
}): ResolvedFieldValues => {
  const resolvedHeight =
    mezzanineDraft && derivedHeight !== undefined
      ? derivedHeight
      : (heightDraft ?? storey.height ?? 0);

  const showBaseElevationInput = showElevationInput && !mezzanineDraft;
  const resolvedElevation = showBaseElevationInput
    ? (elevationDraft ?? storey.elevation ?? 0)
    : undefined;

  const mezzanineElevationValue = mezzanineDraft
    ? (mezzanineElevationDraft ??
      getMezzanineElevationFallback(parentStoreyHeight) ??
      0)
    : undefined;

  return {
    resolvedHeight,
    resolvedElevation,
    mezzanineElevationValue,
    showBaseElevationInput,
  };
};

/**
 * Whether cutouts block enabling mezzanine and the matching tooltip copy.
 * @param params
 * @returns blocked flag and tooltip (empty when not blocked)
 */
const getMezzanineCutoutBlockState = ({
  isGroundFloor,
  storey,
  storeyHasCutouts,
  parentHasCutouts,
}: {
  isGroundFloor: boolean;
  storey: Storey;
  storeyHasCutouts: boolean;
  parentHasCutouts: boolean;
}): MezzanineCutoutBlockState => {
  const blocked =
    !isGroundFloor &&
    !storey.mezzanine &&
    (storeyHasCutouts || parentHasCutouts);
  if (!blocked) return { blocked: false, tooltip: '' };
  if (storeyHasCutouts && parentHasCutouts)
    return {
      blocked: true,
      tooltip:
        'Mezzanine cannot be enabled while this storey and the storey below have cutouts.',
    };
  if (storeyHasCutouts)
    return {
      blocked: true,
      tooltip: 'Mezzanine cannot be enabled while this storey has cutouts.',
    };
  return {
    blocked: true,
    tooltip: 'Mezzanine cannot be enabled while the storey below has cutouts.',
  };
};

type UseStoreySettingsMezzanineToggleParams = {
  storey: Storey;
  storeyBelow: NonMezzanineStorey | undefined;
  isGroundFloor: boolean;
  parentStoreyHeight: number;
  mezzanineDraft: boolean;
  isReadonly: boolean;
  storeyHasCutouts: boolean;
  parentHasCutouts: boolean;
};

/**
 * Mezzanine switch rules for this panel: enable flag, whether the switch may move, and a tooltip when blocked.
 * @param params
 * @returns whether the switch may change and a non-empty tooltip when it is blocked
 */
const useStoreySettingsMezzanineToggle = (
  params: UseStoreySettingsMezzanineToggleParams,
): {
  isMezzanineToggleAllowed: boolean;
  tooltip: string;
} => {
  const isMezzanineEnableAllowed = useMemo(
    () =>
      isMezzanineAllowed({
        storey: params.storey,
        storeyBelow: params.storeyBelow,
        isGroundFloor: params.isGroundFloor,
        parentStoreyHeight: params.parentStoreyHeight,
        nextMezzanine: true,
      }),
    [
      params.storey,
      params.storeyBelow,
      params.isGroundFloor,
      params.parentStoreyHeight,
    ],
  );

  const isMezzanineToggleAllowed = useMemo(
    () =>
      !params.isReadonly && (params.mezzanineDraft || isMezzanineEnableAllowed),
    [params.isReadonly, params.mezzanineDraft, isMezzanineEnableAllowed],
  );

  const tooltip = useMemo(() => {
    if (isMezzanineToggleAllowed) return '';
    if (params.isReadonly) return 'Read-only mode';
    if (params.mezzanineDraft) return '';
    if (params.isGroundFloor)
      return 'Mezzanine cannot be enabled on the ground floor.';
    if (params.parentStoreyHeight < STOREY_MIN_HEIGHT * 2)
      return `The storey below must be at least ${STOREY_MIN_HEIGHT * 2} m high to add a mezzanine.`;
    const { blocked, tooltip: cutoutTooltip } = getMezzanineCutoutBlockState({
      isGroundFloor: params.isGroundFloor,
      storey: params.storey,
      storeyHasCutouts: params.storeyHasCutouts,
      parentHasCutouts: params.parentHasCutouts,
    });
    if (blocked && cutoutTooltip) return cutoutTooltip;
    return 'Mezzanine cannot be enabled for this storey.';
  }, [
    isMezzanineToggleAllowed,
    params.isReadonly,
    params.mezzanineDraft,
    params.isGroundFloor,
    params.parentStoreyHeight,
    params.storey,
    params.storeyHasCutouts,
    params.parentHasCutouts,
  ]);

  return { isMezzanineToggleAllowed, tooltip };
};
